/**
 * Unified Design Token System — Probate Tool
 *
 * Use these tokens instead of hardcoded Tailwind colour classes.
 * Colours are assigned by ROLE, not by section.
 *
 * Usage:  import { t } from '../designTokens';
 *         <div className={t.surface}>…</div>
 *         <p className={t.textPrimary}>…</p>
 */

export const t = {
    // ── Layout surfaces ──────────────────────────────────────
    background:     'bg-slate-50',           // #F8FAFC  page bg
    surface:        'bg-white',              // #FFFFFF  card surfaces
    surfaceMuted:   'bg-slate-50',           // #F8FAFC  recessed / hover
    surfaceElevated:'bg-white shadow-sm',    // elevated card

    // ── Borders ──────────────────────────────────────────────
    border:         'border-slate-200',      // #E2E8F0  standard
    borderMuted:    'border-slate-100',      // #F1F5F9  subtle
    borderStrong:   'border-slate-300',      // #CBD5E1  emphasis

    // ── Text hierarchy ───────────────────────────────────────
    textPrimary:    'text-slate-900',        // #0F172A  headings
    textSecondary:  'text-slate-600',        // #475569  body
    textMuted:      'text-slate-400',        // #94A3B8  labels / tertiary
    textOnDark:     'text-white',            // on dark surfaces
    textOnDarkMuted:'text-slate-400',        // secondary on dark

    // ── Brand ────────────────────────────────────────────────
    brandDark:      'bg-slate-900',          // #0F172A  dark headers
    brandDarkText:  'text-slate-900',        // brand as text
    brandAccent:    'bg-indigo-600',         // #4F46E5  primary CTA
    brandAccentText:'text-indigo-600',       // brand accent as text
    brandAccentHover:'hover:bg-indigo-700',  // CTA hover
    brandAccentSoft:'bg-indigo-50',          // #EEF2FF  light active tint
    brandAccentSoftBorder: 'border-indigo-200',

    // ── Semantic: Success ────────────────────────────────────
    success:        'text-emerald-600',      // #059669  confirmed / done
    successBg:      'bg-emerald-600',        // solid success bg
    successSoft:    'bg-emerald-50',         // #ECFDF5  success surface
    successBorder:  'border-emerald-200',    // success border
    successMuted:   'text-emerald-700',      // darker success text

    // ── Semantic: Warning ────────────────────────────────────
    warning:        'text-amber-600',        // #D97706  attention
    warningBg:      'bg-amber-500',          // solid warning bg
    warningSoft:    'bg-amber-50',           // #FFFBEB  warning surface
    warningBorder:  'border-amber-200',      // warning border
    warningMuted:   'text-amber-700',        // darker warning text

    // ── Semantic: Danger ─────────────────────────────────────
    danger:         'text-red-600',          // #DC2626  error / urgent
    dangerBg:       'bg-red-600',            // solid danger bg
    dangerSoft:     'bg-red-50',             // #FEF2F2  error surface
    dangerBorder:   'border-red-200',        // danger border
    dangerMuted:    'text-red-700',          // darker danger text

    // ── Semantic: Financial impact ───────────────────────────
    impact:         'text-orange-500',       // #F97316  tax / financial
    impactBg:       'bg-orange-500',         // solid impact bg

    // ── Semantic: Info ───────────────────────────────────────
    info:           'text-blue-600',         // #2563EB  informational
    infoBg:         'bg-blue-600',           // solid info bg
    infoSoft:       'bg-blue-50',            // #EFF6FF  info surface
    infoBorder:     'border-blue-200',       // info border
    infoMuted:      'text-blue-700',         // darker info text

    // ── Interactive ──────────────────────────────────────────
    link:           'text-blue-600 hover:text-blue-800',
    buttonPrimary:  'bg-indigo-600 text-white hover:bg-indigo-700 transition-colors',
    buttonSecondary:'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors',
    buttonDanger:   'bg-red-600 text-white hover:bg-red-700 transition-colors',

    // ── Section headers (unified dark) ───────────────────────
    sectionHeader:       'bg-slate-900 text-white',
    sectionHeaderSub:    'text-slate-400',
    sectionHeaderIcon:   'text-slate-400',

    // ── Cards ────────────────────────────────────────────────
    card:           'bg-white border border-slate-200 rounded-xl shadow-sm',
    cardMuted:      'bg-slate-50 border border-slate-200 rounded-xl',

    // ── Step progress tiles ──────────────────────────────────
    stepActive:         'bg-indigo-600 text-white border-indigo-600 ring-4 ring-indigo-50',
    stepActiveLabel:    'text-indigo-600',
    stepCompleted:      'bg-emerald-100 text-emerald-700 border-emerald-300',
    stepCompletedLabel: 'text-slate-700',
    stepInactive:       'bg-white border-slate-200 text-slate-400',
    stepInactiveLabel:  'text-slate-400',

    // ── Severity badge combos ────────────────────────────────
    severityHigh:   'bg-red-50 border-red-200 text-red-800',
    severityMedium: 'bg-amber-50 border-amber-200 text-amber-800',
    severityLow:    'bg-blue-50 border-blue-200 text-blue-800',
} as const;

/**
 * Inline hex values for use in style={{ }} props.
 * Prefer Tailwind classes (above) when possible.
 */
export const hex = {
    brandDark:      '#0F172A',
    brandDarkAlt:   '#111827',
    brandAccent:    '#4F46E5',
    brandAccentHover:'#4338CA',

    textOnDark:     '#FFFFFF',
    textOnDarkMuted:'#94A3B8',
    textMuted:      '#9CA3AF',
    textSubtle:     '#CBD5E1',

    surface:        '#FFFFFF',
    surfaceDark:    '#111827',
    border:         '#E2E8F0',
    borderMuted:    '#D1D5DB',

    success:        '#059669',
    successLight:   '#34D399',
    warning:        '#D97706',
    warningLight:   '#FBBF24',
    danger:         '#DC2626',
    impact:         '#F97316',
    info:           '#60A5FA',
    infoAccent:     '#2563EB',
    infoBadgeBg:    'rgba(96,165,250,0.15)',
} as const;
